import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { DeviceContactsList } from './DeviceContactsList'
import { ManualContactForm } from './ManualContactForm'
import { Country } from '@/data/countries'

interface DeviceContact {
  name?: string[]
  tel?: string[]
  email?: string[]
}

interface AddContactModalProps {
  isOpen: boolean
  mode: 'phone' | 'manual'
  deviceContacts: DeviceContact[]
  name: string
  phoneNumber: string
  email: string
  selectedCountry: Country | null
  isSaving: boolean
  onClose: () => void
  onSelectDeviceContact: (contact: DeviceContact) => void
  onNameChange: (value: string) => void
  onPhoneChange: (fullNumber: string, country: Country) => void
  onEmailChange: (value: string) => void
  onSave: () => void
}

export function AddContactModal({
  isOpen,
  mode,
  deviceContacts,
  name,
  phoneNumber,
  email,
  selectedCountry,
  isSaving,
  onClose,
  onSelectDeviceContact,
  onNameChange,
  onPhoneChange,
  onEmailChange,
  onSave
}: AddContactModalProps) {
  const { t } = useTranslation()

  const handleClose = () => {
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 pb-20 md:pb-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl w-full max-w-md max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-text-primary">
                {mode === 'phone' 
                  ? t('contacts.selectContact', 'Selecionar contacto')
                  : t('contacts.addContact', 'Adicionar contacto')}
              </h2>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-text-secondary" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {mode === 'phone' && deviceContacts.length > 0 ? (
                <DeviceContactsList
                  contacts={deviceContacts}
                  onSelectContact={onSelectDeviceContact}
                />
              ) : (
                <ManualContactForm
                  name={name}
                  phoneNumber={phoneNumber}
                  email={email}
                  selectedCountry={selectedCountry}
                  isSaving={isSaving}
                  onNameChange={onNameChange}
                  onPhoneChange={onPhoneChange}
                  onEmailChange={onEmailChange}
                  onSave={onSave}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

